import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ClientAuditLog, PaginatedResponse } from '../../../models/security.models';

@Component({
  selector: 'app-client-audit-logs',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="audit-logs-container">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">
          <i class="bi bi-clock-history me-2"></i>
          Audit Logs
        </h6>
        <div class="d-flex gap-2">
          <div class="input-group input-group-sm" style="width: 200px;">
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
            <input
              type="text"
              class="form-control"
              placeholder="Search logs..."
              [(ngModel)]="searchTerm"
              (keyup.enter)="searchLogs()">
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            (click)="exportLogs()"
            [disabled]="isLoading">
            <i class="bi bi-download me-1"></i>
            Export
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            (click)="refreshLogs()">
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i class="bi bi-arrow-clockwise me-1" *ngIf="!isLoading"></i>
            Refresh
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="card mb-3">
        <div class="card-body py-2">
          <div class="row align-items-end g-2">
            <div class="col-md-3">
              <label class="form-label form-label-sm">Action Type</label>
              <select
                class="form-select form-select-sm"
                [(ngModel)]="selectedActionType"
                (change)="loadAuditLogs()">
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="LOGIN">Login</option>
                <option value="CONNECTION_TEST">Connection Test</option>
                <option value="CREDENTIALS_GENERATED">Credentials Generated</option>
                <option value="API_REQUEST">API Request</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label form-label-sm">Date From</label>
              <input
                type="date"
                class="form-control form-control-sm"
                [(ngModel)]="dateFrom"
                (change)="loadAuditLogs()">
            </div>
            <div class="col-md-3">
              <label class="form-label form-label-sm">Date To</label>
              <input
                type="date"
                class="form-control form-control-sm"
                [(ngModel)]="dateTo"
                (change)="loadAuditLogs()">
            </div>
            <div class="col-md-3">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary w-100"
                (click)="clearFilters()">
                <i class="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading && auditLogs.length === 0" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading audit logs...</span>
        </div>
        <p class="mt-2 text-muted">Loading audit logs...</p>
      </div>

      <!-- No Data State -->
      <div *ngIf="!isLoading && auditLogs.length === 0" class="text-center py-5">
        <i class="bi bi-clock-history display-4 text-muted"></i>
        <h6 class="mt-3 text-muted">No Audit Logs Found</h6>
        <p class="text-muted">
          {{ hasFilters() ? 'No logs match your current filters.' : 'No audit logs available for this client integration.' }}
        </p>
      </div>

      <!-- Audit Logs Table -->
      <div *ngIf="auditLogs.length > 0" class="card">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th width="120">Timestamp</th>
                <th width="100">Action</th>
                <th width="120">User</th>
                <th>Details</th>
                <th width="100">Status</th>
                <th width="80">IP Address</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let log of auditLogs; trackBy: trackByLogId">
                <td>
                  <small class="text-nowrap">
                    {{ log.timestamp | date:'MMM d, y' }}<br>
                    {{ log.timestamp | date:'h:mm:ss a' }}
                  </small>
                </td>
                <td>
                  <span class="badge" [class]="getActionBadgeClass(log.action)">
                    {{ formatAction(log.action) }}
                  </span>
                </td>
                <td>
                  <div class="text-truncate" style="max-width: 100px;" [title]="log.userEmail || 'System'">
                    <strong>{{ log.userName || 'System' }}</strong><br>
                    <small class="text-muted">{{ log.userEmail || '' }}</small>
                  </div>
                </td>
                <td>
                  <div class="audit-details">
                    <div class="text-break">{{ log.details }}</div>
                    <div *ngIf="log.resourceType" class="mt-1">
                      <small class="text-muted">
                        <i class="bi bi-tag me-1"></i>
                        {{ log.resourceType }}: {{ log.resourceId }}
                      </small>
                    </div>
                    <div *ngIf="log.changes && log.changes.length > 0" class="mt-1">
                      <small class="text-muted">
                        <i class="bi bi-pencil me-1"></i>
                        Changes: {{ log.changes.join(', ') }}
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge" [class]="getStatusBadgeClass(log.success)">
                    {{ log.success ? 'Success' : 'Failed' }}
                  </span>
                  <div *ngIf="log.errorMessage" class="mt-1">
                    <small class="text-danger" [title]="log.errorMessage">
                      <i class="bi bi-exclamation-triangle"></i>
                    </small>
                  </div>
                </td>
                <td>
                  <small class="text-muted text-nowrap">{{ log.ipAddress || 'N/A' }}</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="card-footer" *ngIf="totalPages > 1">
          <nav aria-label="Audit logs pagination">
            <ul class="pagination pagination-sm justify-content-center mb-0">
              <li class="page-item" [class.disabled]="currentPage === 1">
                <a class="page-link" (click)="goToPage(currentPage - 1)" aria-label="Previous">
                  <span aria-hidden="true">&laquo;</span>
                </a>
              </li>
              <li class="page-item" 
                  *ngFor="let page of getPageNumbers()" 
                  [class.active]="page === currentPage">
                <a class="page-link" (click)="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" [class.disabled]="currentPage === totalPages">
                <a class="page-link" (click)="goToPage(currentPage + 1)" aria-label="Next">
                  <span aria-hidden="true">&raquo;</span>
                </a>
              </li>
            </ul>
          </nav>
          <div class="text-center mt-2">
            <small class="text-muted">
              Showing {{ ((currentPage - 1) * pageSize) + 1 }}-{{ getMaxDisplayCount() }} 
              of {{ totalCount }} logs
            </small>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .audit-logs-container {
      height: 100%;
    }
    
    .form-label-sm {
      font-size: 0.8rem;
      margin-bottom: 0.25rem;
    }
    
    .audit-details {
      max-width: 300px;
    }
    
    .table td {
      vertical-align: middle;
    }
    
    .page-link {
      cursor: pointer;
    }
    
    .page-item.disabled .page-link {
      cursor: not-allowed;
    }
  `]
})
export class ClientAuditLogsComponent implements OnInit, OnDestroy {
  @Input() clientId!: string;

  private destroy$ = new Subject<void>();
  
  // Data
  auditLogs: ClientAuditLog[] = [];
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 10;
  
  // Loading state
  isLoading = false;
  
  // Filters
  searchTerm = '';
  selectedActionType = '';
  dateFrom = '';
  dateTo = '';

  constructor(private clientIntegrationService: ClientIntegrationService) {}

  ngOnInit(): void {
    if (this.clientId) {
      this.loadAuditLogs();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAuditLogs(): void {
    if (!this.clientId) return;

    this.isLoading = true;
    
    this.clientIntegrationService.getAuditLogs(this.clientId, this.currentPage, this.pageSize)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response: PaginatedResponse<ClientAuditLog>) => {
          console.log('Audit logs response:', response); // Debug log
          this.auditLogs = response.items || [];
          this.totalCount = response.totalCount || 0;
          this.totalPages = response.totalPages || 0;
          this.currentPage = response.pageNumber || 1;
        },
        error: (error: any) => {
          console.warn('Audit logs API not available, using mock data:', error);
          // Handle gracefully - logs might not be implemented yet, use mock data
          this.auditLogs = this.getMockAuditLogs();
          this.totalCount = this.auditLogs.length;
          this.totalPages = Math.ceil(this.totalCount / this.pageSize);
          this.currentPage = 1;
        }
      });
  }

  refreshLogs(): void {
    this.currentPage = 1;
    this.loadAuditLogs();
  }

  searchLogs(): void {
    this.currentPage = 1;
    this.loadAuditLogs();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedActionType = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.currentPage = 1;
    this.loadAuditLogs();
  }

  exportLogs(): void {
    if (!this.clientId) return;

    this.clientIntegrationService.exportAuditLogs(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `audit-logs-${this.clientId}-${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: (error: any) => {
          console.warn('Export audit logs API not available:', error);
          // Create a simple CSV export from current data
          this.exportCurrentLogsAsCSV();
        }
      });
  }

  private exportCurrentLogsAsCSV(): void {
    const headers = ['Timestamp', 'Action', 'User', 'Details', 'Status', 'IP Address'];
    const csvContent = [
      headers.join(','),
      ...this.auditLogs.map(log => [
        log.timestamp,
        log.action,
        log.userName || 'System',
        `"${log.details.replace(/"/g, '""')}"`, // Escape quotes in details
        log.success ? 'Success' : 'Failed',
        log.ipAddress || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${this.clientId}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadAuditLogs();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  hasFilters(): boolean {
    return !!(this.searchTerm || this.selectedActionType || this.dateFrom || this.dateTo);
  }

  getMaxDisplayCount(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  trackByLogId(_index: number, log: ClientAuditLog): string {
    return log.id;
  }

  getActionBadgeClass(action: string): string {
    switch (action.toUpperCase()) {
      case 'CREATE': return 'bg-success';
      case 'UPDATE': return 'bg-info';
      case 'DELETE': return 'bg-danger';
      case 'LOGIN': return 'bg-primary';
      case 'CONNECTION_TEST': return 'bg-warning';
      case 'CREDENTIALS_GENERATED': return 'bg-secondary';
      case 'API_REQUEST': return 'bg-light text-dark';
      default: return 'bg-secondary';
    }
  }

  getStatusBadgeClass(success: boolean): string {
    return success ? 'bg-success' : 'bg-danger';
  }

  formatAction(action: string): string {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  private getMockAuditLogs(): ClientAuditLog[] {
    return [
      {
        id: '1',
        clientId: this.clientId,
        action: 'CREATE',
        details: 'Client integration created with initial configuration',
        timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
        userName: 'Admin User',
        userEmail: 'admin@rarr.com',
        userId: '1',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0...',
        success: true,
        resourceType: 'ClientIntegration',
        resourceId: this.clientId,
        changes: ['clientName', 'clientType', 'description'],
        errorMessage: undefined
      },
      {
        id: '2',
        clientId: this.clientId,
        action: 'CONNECTION_TEST',
        details: 'Connection test executed successfully',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        userName: 'Admin User',
        userEmail: 'admin@rarr.com',
        userId: '1',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0...',
        success: true,
        resourceType: 'ClientIntegration',
        resourceId: this.clientId,
        changes: [],
        errorMessage: undefined
      },
      {
        id: '3',
        clientId: this.clientId,
        action: 'CREDENTIALS_GENERATED',
        details: 'API credentials generated for client',
        timestamp: new Date(Date.now() - 43200000).toISOString(),
        userName: 'Admin User',
        userEmail: 'admin@rarr.com',
        userId: '1',
        ipAddress: '192.168.1.100',
        userAgent: 'Mozilla/5.0...',
        success: true,
        resourceType: 'ClientCredentials',
        resourceId: this.clientId,
        changes: ['apiAccount', 'apiSecretKey'],
        errorMessage: undefined
      },
      {
        id: '4',
        clientId: this.clientId,
        action: 'API_REQUEST',
        details: 'Client made API request to /api/orders/create',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        userName: 'Client System',
        userEmail: undefined,
        userId: this.clientId,
        ipAddress: '203.0.113.1',
        userAgent: 'ClientApp/1.0',
        success: true,
        resourceType: 'ApiRequest',
        resourceId: 'req_' + Date.now(),
        changes: [],
        errorMessage: undefined
      },
      {
        id: '5',
        clientId: this.clientId,
        action: 'API_REQUEST',
        details: 'Failed API request to /api/orders/update - Invalid authentication',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        userName: 'Client System',
        userEmail: undefined,
        userId: this.clientId,
        ipAddress: '203.0.113.1',
        userAgent: 'ClientApp/1.0',
        success: false,
        resourceType: 'ApiRequest',
        resourceId: 'req_' + (Date.now() - 1800000),
        changes: [],
        errorMessage: 'Invalid authentication token provided'
      }
    ];
  }
}